<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="programs-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('program.title') }}</h1>
          <p class="page-subtitle">{{ $t('program.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="openAddModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('program.addProgram') }}</span>
        </IntercomButton>
      </div>

      <!-- Programs Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="programs"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-program_number="{ value }">
            <span class="program-number">{{ value }}</span>
          </template>
          <template #cell-program_type="{ value }">
            <IntercomBadge :variant="getTypeVariant(value)">
              {{ $t(`program.types.${value}`) }}
            </IntercomBadge>
          </template>
          <template #cell-metric_label="{ row }">
            <span class="metric-label">{{ row.metric_label }}</span>
            <span class="metric-unit"> ({{ row.metric_unit }})</span>
          </template>
          <template #cell-is_default="{ value }">
            <IntercomBadge v-if="value" variant="info" size="sm">
              {{ $t('program.default') }}
            </IntercomBadge>
          </template>
          <template #cell-is_active="{ value }">
            <IntercomBadge :variant="value ? 'success' : 'default'" size="sm">
              {{ value ? $t('common.active') : $t('common.inactive') }}
            </IntercomBadge>
          </template>
        </IntercomTable>
        <p v-if="!isLoading && programs.length === 0" class="empty-text">
          {{ $t('program.noPrograms') }}
        </p>
      </IntercomCard>

      <!-- Add/Edit Modal -->
      <IntercomModal
        v-model:show="showModal"
        :title="editingProgram ? $t('program.editProgram') : $t('program.addProgram')"
      >
        <ProgramForm
          :program="editingProgram"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import ProgramForm from '../components/ProgramForm.vue'
import { useNavigation } from '../composables/useNavigation.js'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listPrograms, createProgram, updateProgram } = useSeva()
const { navSections } = useNavigation()

const programs = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const showModal = ref(false)
const editingProgram = ref(null)

const columns = [
  { key: 'program_number', title: t('program.number'), width: '120px' },
  { key: 'name', title: t('program.name'), width: '200px' },
  { key: 'program_type', title: t('program.type'), width: '140px' },
  { key: 'metric_label', title: t('program.metricLabel'), width: '180px' },
  { key: 'is_default', title: '', width: '80px' },
  { key: 'is_active', title: t('common.active'), width: '90px' },
]

const getTypeVariant = (type) => {
  switch (type) {
    case 'food_distribution': return 'success'
    case 'pilgrimage': return 'info'
    case 'healthcare': return 'warning'
    case 'education': return 'default'
    default: return 'default'
  }
}

const fetchPrograms = async () => {
  isLoading.value = true
  try {
    const response = await listPrograms()
    // apiClient.get() unwraps to { programs: [...] }
    programs.value = response?.programs || []
  } catch (error) {
    console.error('Failed to fetch programs:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const openAddModal = () => {
  editingProgram.value = null
  showModal.value = true
}

const handleRowClick = (program) => {
  editingProgram.value = program
  showModal.value = true
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    if (editingProgram.value) {
      await updateProgram(editingProgram.value.id, formData)
      toast.success(t('success.programUpdated'))
    } else {
      await createProgram(formData)
      toast.success(t('success.programCreated'))
    }
    closeModal()
    fetchPrograms()
  } catch (error) {
    console.error('Failed to save program:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showModal.value = false
  editingProgram.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchPrograms()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.programs-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.program-number {
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.metric-label {
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.metric-unit {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .programs-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }
}
</style>
